import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthService from "../../services/authService";
import toast from "react-hot-toast";
import Hls from "hls.js";
import LearnService from '../../services/LearnService';
import { trackFacebookEvent } from "../../services/facebookMetaEents";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";

const SubscriptionPaywall = () => {
  const navigate = useNavigate();
  const { processId } = useParams();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [promotionalData, setPromotionalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [subscriptionResponse, setSubscriptionResponse] = useState(null);
  const [showSubscribeOverlay, setShowSubscribeOverlay] = useState(false);
  const [showTapToMute, setShowTapToMute] = useState(true); // Show label initially
  const [hasUserUnmuted, setHasUserUnmuted] = useState(false); // Track if user unmuted

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await LearnService.getPromotionalDetail();
        const data = res.data || {};
        setPromotionalData(data);
      } catch (e) {
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Use premiumThumbnails from API data
  const courseThumbnails = promotionalData?.premiumThumbnails || [];

  const courses = courseThumbnails.map((img, idx) => ({
    title: `Course ${idx + 1}`,
    img: img,
  }));

  // Use payWallVideo from API data
  const payWallVideo = promotionalData?.payWallVideo;

  // Use comparePrice and price from API data
  const comparePrice = promotionalData?.comparePrice;
  const price = promotionalData?.authInfo?.amount;

  // Use subscriptionInfo for recurring price
  const recurringAmount = promotionalData?.subscriptionInfo?.amount;
  const recurringPeriod = promotionalData?.subscriptionInfo?.validity;
  const recurringPeriodType = promotionalData?.subscriptionInfo?.validityType;

  const handleRazorpayPayment = async () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      navigate(`/learn/login/${processId}`);
      return;
    }

    // Check for PRO membership
    const userDetail = JSON.parse(localStorage.getItem("user_detail") || "{}");
    const profileDetails = JSON.parse(localStorage.getItem("profileDetail") || "{}");

    if (
      userDetail?.user?.membership === "PRO" ||
      profileDetails?.userId?.membership === "PRO"
    ) {
      // Already PRO, go to subscribe screen
      navigate("/subscribe-screen");
      return;
    }

    // Not PRO, proceed with payment as before
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    try {
      setIsButtonDisabled(true);

      const userDetail = JSON.parse(
        localStorage.getItem("user_detail") || "{}"
      );
      const subscriptionBody = {
        planId: promotionalData?.planId,
        provider: "razorpay",
        authAmount: promotionalData?.authInfo?.amount,
        validity: promotionalData?.authInfo?.validity,
        validityType: promotionalData?.authInfo?.validityType,
        sourceId: processId,
        sourceType: "process",
        itemId: promotionalData?.itemId,
        providerId: "1",
      };

      const subscriptionResponse = await AuthService.createCourseSubscription(
        subscriptionBody
      );
      console.log("Subscription created:", subscriptionResponse);

      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        name: "Creedom",
        order_id: subscriptionResponse?.data?.subscriptionDetails?.id,
        customer_id:
          subscriptionResponse?.data?.subscriptionDetails?.customerId,
        recurring: "1",
        notes: {
          userId: userDetail?.user?._id,
          userReferenceId:
            subscriptionResponse?.data?.subscriptionDetails?.customerId,
          razorpayOrderId: subscriptionResponse?.data?.subscriptionDetails?.id,
        },
        theme: { color: "#3399cc" },
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          emi: false,
          emandate: false,
          paylater: false,
        },
        prefill: {
          email:
            userDetail?.user?.emailId ||
            `${userDetail?.user?.phoneNumber}@gmail.com`,
          contact: userDetail?.user?.phoneNumber,
          name: userDetail?.user?.userName,
        },
        handler: function (response) {
          // Handle payment success
          toast.success("Payment successful!");
          setIsButtonDisabled(false);
          console.log("Payment response:", response);
          // Update user profile data in localStorage after successful payment
          updateUserProfileAfterPayment();
          // Navigate to success screen
          navigate("/subscription-success");
        },
        modal: {
          ondismiss: function () {
            toast("Payment popup closed");
            setIsButtonDisabled(false);
          },
        },
      });
      rzp.open();
    } catch (error) {
      console.error("Payment initiation failed:", error);
      toast.error("Payment initiation failed");
      setIsButtonDisabled(false);
    }
  };

  // Function to update user profile data after successful payment
  const updateUserProfileAfterPayment = async () => {
    try {
      const userProfileFilterData = JSON.parse(
        localStorage.getItem("user_profile_filter_data") || "{}"
      );
      const profileId = userProfileFilterData?._id;

      if (profileId) {
        // Fetch updated user profile data using profileId
        const updatedUserData = await AuthService.getProfileDetail(profileId);

        // Update localStorage with new user data (including PRO membership)
        localStorage.setItem("profileDetail", JSON.stringify(updatedUserData));

        console.log(
          "User profile updated with PRO membership:",
          updatedUserData
        );
      } else {
        console.error("Profile ID not found in user_profile_filter_data");
      }
    } catch (error) {
      console.error("Error updating user profile after payment:", error);
    }
  };


  const handlePurchase = (amount) => {
    trackFacebookEvent("Purchase", {
      value: amount,
      currency: "INR",
    });
  };

  useEffect(() => {
    if (loading) return;
    if (!videoRef.current || !subscriptionResponse || !subscriptionResponse?.payWallVideo) return;

    const video = videoRef.current;
    const videoUrl = subscriptionResponse.payWallVideo;

    if (isIOS()) {
      video.src = videoUrl;
      video.muted = muted;
      video.play().catch((e) => {
        console.log('Autoplay prevented:', e);
      });
    } else if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        video.muted = muted;
        video.play().catch((e) => {
          console.log('Autoplay prevented:', e);
        });
      });
      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoUrl;
      video.muted = muted;
      video.play().catch((e) => {
        console.log('Autoplay prevented:', e);
      });
    }
  }, [subscriptionResponse, loading, muted]);

  useEffect(() => {
    if (videoRef.current) {
      if (showSubscribeOverlay && !videoRef.current.paused) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else if (
        !showSubscribeOverlay &&
        isPlaying &&
        videoRef.current.paused
      ) {
        videoRef.current.play().catch(() => { });
      }
    }
  }, [showSubscribeOverlay, isPlaying]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      } else {
        // Optionally resume playback:
        // if (videoRef.current && isPlaying) {
        //   videoRef.current.play().catch(() => {});
        // }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (
      !loading &&
      promotionalData &&
      localStorage.getItem("auto_initiate_payment") === "1"
    ) {
      localStorage.removeItem("auto_initiate_payment");
      handleRazorpayPayment();
    }
    // eslint-disable-next-line
  }, [loading, promotionalData]);

  // On initial load, mute for 5 seconds, then auto-unmute if user hasn't unmuted
  useEffect(() => {
    if (!hasUserUnmuted && muted) {
      setShowTapToMute(true);
      const timer = setTimeout(() => {
        setMuted(false);
        setShowTapToMute(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [muted, hasUserUnmuted]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else if (!isPlaying && videoRef.current.paused) {
      videoRef.current.play().catch(() => { });
      setIsPlaying(true);
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);

  if (loading) {
    return (
      <div
        style={{
          background: "#111",
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          color: "#fff",
        }}
      >
        Loading subscription details...
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#111",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 0,
        backgroundColor: "#000"
      }}
    >
      {/* Top Card */}
      <div
        style={{
          background: "#222",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 8px 32px #0004",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Video/Image */}
        <div
          style={{
            width: "100%",
            height: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            loop={false}
            muted={muted}
            playsInline
            style={{ width: "100%", height: 250 }}
            src={payWallVideo || undefined}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
          />
          {/* Centered Play/Pause Button Overlay */}
          <button
            onClick={handlePlayPause}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(0,0,0,0.6)",
              border: "none",
              borderRadius: "50%",
              width: 64,
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              cursor: "pointer",
              outline: "none",
            }}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg width="36" height="36" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="18" fill="rgba(0,0,0,0.4)" />
                <rect x="11" y="10" width="4" height="16" fill="#fff" />
                <rect x="21" y="10" width="4" height="16" fill="#fff" />
              </svg>
            ) : (
              <svg width="36" height="36" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="18" fill="rgba(0,0,0,0.4)" />
                <polygon points="14,10 28,18 14,26" fill="#fff" />
              </svg>
            )}
          </button>
          {/* Mute/Unmute Button (top-left, like landing screen) */}
          <div
            style={{
              position: "absolute",
              top: 20,
              left: 20,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => {
                if (muted) {
                  setMuted(false);
                  setShowTapToMute(false);
                  setHasUserUnmuted(true);
                  if (videoRef.current) {
                    videoRef.current.muted = false;
                    videoRef.current.play().catch(() => {});
                  }
                } else {
                  setMuted(true);
                  if (videoRef.current) {
                    videoRef.current.muted = true;
                  }
                }
              }}
              style={{
                background: "rgba(0,0,0,0.6)",
                border: "none",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                cursor: "pointer",
              }}
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
            </button>
            {muted && showTapToMute && !hasUserUnmuted && (
              <span
                onClick={() => {
                  setMuted(false);
                  setShowTapToMute(false);
                  setHasUserUnmuted(true);
                  if (videoRef.current) {
                    videoRef.current.muted = false;
                    videoRef.current.play().catch(() => {});
                  }
                }}
                style={{
                  marginLeft: 10,
                  background: "rgba(0,0,0,0.7)",
                  color: "#fff",
                  padding: "6px 14px",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px #0003",
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                Tap to unmute
              </span>
            )}
          </div>
        </div>
        {/* Pricing and Info */}
        <div style={{ padding: "32px 24px 16px 24px", background: "#000" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: 16,
              color: "#fff",
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            <span
              style={{
                borderBottom: "2px solid #333",
                paddingBottom: 4,
                fontFamily: "Gilroy-Bold, sans-serif",
              }}
            >
              {"UNLOCK ALL SERIES"}
            </span>
          </div>
          <div style={{ textAlign: "center", color: "#fff" }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>Pay only</div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                margin: "0 0 8px 0",
                lineHeight: 1,
                fontFamily: "Gilroy-Bold, sans-serif",
              }}
            >
              <span
                style={{
                  fontSize: 40,
                  background: 'linear-gradient(90deg, #FFFFFF 0%, #6F6F6F 50%, #FFFFFF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                  display: 'inline-block',
                }}
              >
                ₹{price}
              </span>
            </div>
            <div
              style={{
                fontSize: 18,
                marginBottom: 8,
                fontFamily: "Gilroy-Regular, sans-serif",
              }}
            >
              for {promotionalData?.authInfo?.validity} {promotionalData?.authInfo?.validityType} and become better singer
            </div>
            <div
              style={{
                fontSize: 16,
                color: "#aaa",
                marginBottom: 4,
                fontFamily: "Gilroy-Regular, sans-serif",
              }}
            >
              Then {recurringAmount && (
                <span
                  style={{
                    fontWeight: 600,
                    fontFamily: "Gilroy-Bold, sans-serif",
                  }}
                >
                  ₹{recurringAmount}/{recurringPeriodType}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#4ec3fa",
                marginBottom: 8,
                fontFamily: "Gilroy-Regular, sans-serif",
              }}
            >
              {"You can cancel anytime"}
            </div>
          </div>
        </div>
      </div>

      {/* Course Cards */}
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          overflowX: "auto",
          display: "flex",
          gap: 12,
          padding: "24px 0 16px 0",
          margin: "0 auto",
        }}
      >
        {courses.map((course, idx) => (
          <div
            key={idx}
            style={{
              minWidth: 110,
              height: 160,
              background: "#222",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 2px 8px #0002",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              marginLeft: idx === 0 ? 24 : 0,
              marginRight: idx === courses.length - 1 ? 24 : 0,
            }}
          >
            <img
              src={course.img}
              alt={course.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        ))}
      </div>

      {/* Start Learning Button */}
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          padding: "20px 20px",
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          background: '#000',
        }}
      >
        <button
          style={{
            width: "100%",
            padding: "10px 15px",
            borderRadius: 10,
            border: "none",
            fontWeight: 500,
            fontSize: 20,
            color: "#fff",
            background: "linear-gradient(90deg, #EE35FF 0%, #FF8761 100%)",
            boxShadow: "0 2px 8px #0002",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
            letterSpacing: 1,
            outline: "none",
            cursor: "pointer",
            fontFamily: "Gilroy-Bold",
          }}
          onClick={handleRazorpayPayment}
          disabled={isButtonDisabled}
        >
          <span
            style={{ fontWeight: 500, fontSize: 20, fontFamily: "Gilroy-Bold" }}
          >
            Start learning
          </span>
          <span
            style={{ fontWeight: 500, fontSize: 28, fontFamily: "Gilroy-Bold" }}
          >
            ₹{price}
          </span>
        </button>
        <span
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'center',
            fontWeight: 500,
            fontSize: 12,
            fontFamily: 'Gilroy-Bold',
            color: '#fff',
            paddingTop: 10,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/learn')}
        >
          Explore other series
        </span>
      </div>
    </div>
  );
};

function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Macintosh") && "ontouchend" in document)
  );
}

export default SubscriptionPaywall;
